import React, { ReactNode, useEffect, useRef, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const hasTriggeredRedirectRef = useRef(false);
  const hasTriggeredReauthRef = useRef(false);
  const [isVerifyingAuth, setIsVerifyingAuth] = useState(false);
  const [callbackTimedOut, setCallbackTimedOut] = useState(false);

  // Paths that can be accessed without authentication
  const allowUnauthenticatedPaths = ["/admin/invite/accept", "/admin/no-hotels"];
  const isAllowedAnonymousRoute = allowUnauthenticatedPaths.some((path) =>
    location.pathname.startsWith(path),
  );

  const recentCallback = useMemo(() => {
    try {
      const ts = sessionStorage.getItem("AUTH_POST_CALLBACK_TS");
      if (!ts) return false;
      const ageMs = Date.now() - Number(ts);
      return ageMs >= 0 && ageMs < 5000; // within 5s of callback
    } catch {
      return false;
    }
  }, []);

  // Match Auth0 SDK's hasAuthParams: (code || error) && state — both must be present.
  // Previously this was `code || state` (either alone was enough), which is looser than
  // the SDK's detection and could classify URLs as callbacks that Auth0 would never process.
  const params = new URLSearchParams(location.search);
  const isAuthCallback =
    (params.has("code") || params.has("error")) && params.has("state");

  // Safety-net timeout: if we appear to be in a callback/recent-callback state but
  // isAuthenticated never becomes true within 10 seconds, assume the state is stale
  // and redirect to the no-hotels page. This is a generalized safety net — it covers
  // stale AUTH_POST_CALLBACK_TS markers, stale code/state URL params, and any other
  // edge case where ProtectedRoute would otherwise show "Completing sign-in..." forever.
  //
  // The timeout is intentionally scoped to avoid interfering with legitimate flows:
  // - Does NOT fire on anonymous routes (invite acceptance, no-hotels)
  // - Does NOT fire while Auth0 is actively loading (legitimate sign-in in progress)
  // - Does NOT fire when EXTERNAL_PAYMENT_REDIRECT is present (Stripe return flow
  //   has its own re-auth mechanism at lines 87-108 below)
  // Check both existence AND freshness of the payment redirect marker.
  // A stale marker (from a Stripe redirect 30+ minutes ago that was never cleaned up)
  // should NOT suppress the timeout — only a recent marker within the 5-minute window
  // that the re-auth flow at lines 113-127 also uses.
  const hasPaymentRedirectMarker = (() => {
    try {
      const ts = sessionStorage.getItem("EXTERNAL_PAYMENT_REDIRECT");
      if (!ts) return false;
      const ageMs = Date.now() - Number(ts);
      return ageMs >= 0 && ageMs < 300000; // 5-minute TTL, matches re-auth check below
    } catch { return false; }
  })();

  useEffect(() => {
    if (
      (isAuthCallback || recentCallback) &&
      !isAuthenticated &&
      !isLoading &&
      !isAllowedAnonymousRoute &&
      !hasPaymentRedirectMarker
    ) {
      const timeout = setTimeout(() => {
        setCallbackTimedOut(true);
        // Clean stale session markers that triggered this timeout in the first place
        try {
          sessionStorage.removeItem("AUTH_POST_CALLBACK_TS");
          sessionStorage.removeItem("EXTERNAL_PAYMENT_REDIRECT");
        } catch { /* ignore */ }
        // Preserve route context (matches the normal unauthenticated redirect at line ~133)
        const returnTo = encodeURIComponent(location.pathname + location.search);
        // Clean stale code/state from URL to prevent re-triggering on next render
        window.history.replaceState({}, document.title, location.pathname);
        navigate(`/admin/no-hotels?returnTo=${returnTo}`, { replace: true });
      }, 10000);
      return () => clearTimeout(timeout);
    }
  }, [isAuthCallback, recentCallback, isAuthenticated, isLoading, isAllowedAnonymousRoute, hasPaymentRedirectMarker, navigate, location.pathname]);

  // Verify authentication using token fetch when Auth0 state seems unauthenticated.
  // This handles the race condition where isAuthenticated is briefly false after
  // external redirects (e.g., returning from external payment) while Auth0 is still
  // restoring the session. getAccessTokenSilently will succeed if session is valid.
  const verifyAuthentication = useCallback(async (): Promise<boolean> => {
    try {
      await getAccessTokenSilently();
      return true; // Token fetch succeeded - user is authenticated
    } catch {
      return false; // Token fetch failed - user is not authenticated
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (isAuthCallback) {
      return; // Let Auth0Provider finish processing
    }

    if (!isLoading && !isAuthenticated) {
      // Avoid redirect loops or multiple triggers per mount
      if (
        recentCallback ||
        hasTriggeredRedirectRef.current ||
        isAllowedAnonymousRoute ||
        isVerifyingAuth
      ) {
        return;
      }

      // Auth0 state says not authenticated, but verify with token fetch.
      // This handles race conditions after external redirects (e.g., external payment).
      setIsVerifyingAuth(true);
      verifyAuthentication().then((isActuallyAuthenticated) => {
        setIsVerifyingAuth(false);
        if (!isActuallyAuthenticated && !hasTriggeredRedirectRef.current) {
          // Check if user was returning from external payment
          // Auth0 session cookies can be lost after cross-origin navigation
          const externalPaymentTs = sessionStorage.getItem("EXTERNAL_PAYMENT_REDIRECT");
          if (externalPaymentTs && !hasTriggeredReauthRef.current) {
            const ageMs = Date.now() - Number(externalPaymentTs);
            // If marker is recent (within 5 minutes), re-authenticate through Auth0
            if (ageMs >= 0 && ageMs < 300000) {
              hasTriggeredReauthRef.current = true;
              sessionStorage.removeItem("EXTERNAL_PAYMENT_REDIRECT");
              // Redirect through Auth0 - user is still logged in on Auth0.com, so this should be instant
              loginWithRedirect({
                appState: { returnTo: location.pathname + location.search },
              });
              return;
            }
            // Marker too old, remove it
            sessionStorage.removeItem("EXTERNAL_PAYMENT_REDIRECT");
          }

          hasTriggeredRedirectRef.current = true;
          const returnTo = encodeURIComponent(location.pathname + location.search);
          // Redirect to NoHotelsPage - user is genuinely not authenticated
          navigate(`/admin/no-hotels?returnTo=${returnTo}`, { replace: true });
        }
        // If authenticated, don't redirect - Auth0 state will update soon
      });
    }
  }, [
    isLoading,
    isAuthenticated,
    navigate,
    location,
    recentCallback,
    isAllowedAnonymousRoute,
    isVerifyingAuth,
    verifyAuthentication,
    loginWithRedirect,
  ]);

  // Clean up stale EXTERNAL_PAYMENT_REDIRECT marker when authentication
  // restores cleanly (e.g., user returns from Stripe and Auth0 session is still
  // valid). Without this, the marker lingers in sessionStorage until the next
  // logout, suppressing the timeout safety-net on unrelated future auth states.
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      try {
        sessionStorage.removeItem("EXTERNAL_PAYMENT_REDIRECT");
      } catch { /* ignore */ }
    }
  }, [isAuthenticated, isLoading]);

  // When Auth0 is loading or we're verifying auth, render children to let them
  // handle the loading state via AppBootstrapOverlay.
  if (isLoading || isVerifyingAuth) {
    return <>{children}</>;
  }

  if (!isAuthenticated) {
    // Allow anonymous routes to render their children
    if (isAllowedAnonymousRoute) {
      return <>{children}</>;
    }
    // If the callback timed out, the timeout effect already navigated away — don't
    // render the stuck "Completing sign-in..." screen.
    if (callbackTimedOut) {
      return null;
    }
    // Show brief message while redirecting to NoHotelsPage or completing sign-in
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        {isAuthCallback || recentCallback
          ? "Completing sign-in..."
          : "Loading..."}
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
